import { TextSelection, type EditorState, type Transaction } from "@tiptap/pm/state";

/**
 * react-email parses every plain `<div>` into an isolating `div` node, and
 * signatures / pasted / quoted HTML usually put each line in its own `<div>`.
 * ProseMirror refuses to join textblocks across an isolating boundary, so
 * Backspace at the start of such a line (or Delete at the end of the one
 * above) did nothing. These treat plain divs as transparent and join the two
 * lines anyway. Anything else (sections, columns, tables, list items, atoms
 * like the suggested-times widget) still blocks, as before.
 */
const TRANSPARENT = new Set(["div"]);

/** Backspace at the very start of a textblock whose previous line lives across a div boundary. */
export function joinBackwardAcrossWrappers(state: EditorState): Transaction | null {
    const { $from, empty } = state.selection;
    if (!empty || !$from.parent.isTextblock || $from.parentOffset !== 0) return null;

    // Climb out of wrappers we're the first child of.
    let d = $from.depth - 1;
    while (d > 0 && $from.index(d) === 0) {
        if (!TRANSPARENT.has($from.node(d).type.name)) return null;
        d--;
    }
    const index = $from.index(d);
    if (index === 0) return null;
    const climbed = $from.depth - 1 - d;

    // Descend into the previous sibling's last line.
    let prev = $from.node(d).child(index - 1);
    let descended = 0;
    while (TRANSPARENT.has(prev.type.name) && prev.lastChild) {
        prev = prev.lastChild;
        descended++;
    }
    if (!prev.isTextblock) return null;
    // Plain sibling paragraphs: ProseMirror's default join already works.
    if (climbed === 0 && descended === 0) return null;

    // End of prev's text = after the sibling, minus its closing tokens.
    const prevTextEnd = $from.before(d + 1) - 1 - descended;
    const tr = state.tr.delete(prevTextEnd, $from.pos);
    tr.setSelection(TextSelection.create(tr.doc, prevTextEnd));
    return tr.scrollIntoView();
}

/** Delete at the very end of a textblock whose next line lives across a div boundary. */
export function joinForwardAcrossWrappers(state: EditorState): Transaction | null {
    const { $from, empty } = state.selection;
    if (!empty || !$from.parent.isTextblock || $from.parentOffset !== $from.parent.content.size) return null;

    let d = $from.depth - 1;
    while (d > 0 && $from.index(d) === $from.node(d).childCount - 1) {
        if (!TRANSPARENT.has($from.node(d).type.name)) return null;
        d--;
    }
    const index = $from.index(d);
    if (index === $from.node(d).childCount - 1) return null;
    const climbed = $from.depth - 1 - d;

    let next = $from.node(d).child(index + 1);
    let descended = 0;
    while (TRANSPARENT.has(next.type.name) && next.firstChild) {
        next = next.firstChild;
        descended++;
    }
    if (!next.isTextblock) return null;
    if (climbed === 0 && descended === 0) return null;

    const nextTextStart = $from.after(d + 1) + 1 + descended;
    const tr = state.tr.delete($from.pos, nextTextStart);
    tr.setSelection(TextSelection.create(tr.doc, $from.pos));
    return tr.scrollIntoView();
}
